import React, {useEffect, useState, useSyncExternalStore} from 'react'
import {type ChatBskyConvoDefs} from '@atproto/api'
import {useQueryClient} from '@tanstack/react-query'

import {useAppState} from '#/lib/hooks/useAppState'
import {Convo} from '#/state/messages/convo/agent'
import {ChatContext} from '#/state/messages/convo/index'
import {useMessagesEventBus} from '#/state/messages/events'
import {
  RQKEY as getConvoKey,
  useMarkAsReadMutation,
} from '#/state/queries/messages/conversation'
import {RQKEY_ROOT as ListConvosQueryKeyRoot} from '#/state/queries/messages/list-conversations'
import {RQKEY as createProfileQueryKey} from '#/state/queries/profile'
import {useAgent} from '#/state/session'

/**
 * ConvoProvider adapted for floating chat windows.
 * Uses `isVisible` prop instead of React Navigation's `useFocusEffect`
 * to manage the Convo agent lifecycle.
 */
export function FloatingConvoProvider({
  children,
  convoId,
  isVisible,
}: {
  children: React.ReactNode
  convoId: string
  isVisible: boolean
}) {
  const queryClient = useQueryClient()
  const agent = useAgent()
  const events = useMessagesEventBus()

  const [convo] = useState(() => {
    const placeholder = queryClient.getQueryData<ChatBskyConvoDefs.ConvoView>(
      getConvoKey(convoId),
    )
    return new Convo({
      convoId,
      agent,
      events,
      placeholderData: placeholder ? {convo: placeholder} : undefined,
    })
  })

  const service = useSyncExternalStore(convo.subscribe, convo.getSnapshot)
  const {mutate: markAsRead} = useMarkAsReadMutation()

  const appState = useAppState()
  const isActive = appState === 'active'

  // Manage lifecycle based on visibility instead of navigation focus
  useEffect(() => {
    if (isActive && isVisible) {
      convo.resume()
      markAsRead({convoId})

      return () => {
        convo.background()
        markAsRead({convoId})
      }
    } else {
      convo.background()
    }
  }, [isActive, isVisible, convo, convoId, markAsRead])

  // Block-state invalidation (same as original ConvoProvider)
  useEffect(() => {
    return convo.on(event => {
      switch (event.type) {
        case 'invalidate-block-state': {
          for (const did of event.accountDids) {
            queryClient.invalidateQueries({
              queryKey: createProfileQueryKey(did),
            })
          }
          queryClient.invalidateQueries({
            queryKey: [ListConvosQueryKeyRoot],
          })
        }
      }
    })
  }, [convo, queryClient])

  return <ChatContext.Provider value={service}>{children}</ChatContext.Provider>
}
